import { AiParsingResult } from '../types'

class AiPdfParsingService {
  private baseUrl = '/api/ai-pdf-parsing'

  /**
   * Parse PDF application file using AI
   */
  async parseApplication(file: File): Promise<AiParsingResult> {
    console.log('🌐 AiPdfParsingService.parseApplication called with file:', file.name, 'Size:', file.size)
    
    try {
      const formData = new FormData()
      formData.append('applicationFile', file)

      console.log('📡 Making API call to:', `${this.baseUrl}/parse-application`)
      
      const response = await fetch(`${this.baseUrl}/parse-application`, {
        method: 'POST',
        body: formData,
        credentials: 'include' // Include session cookies
      })

      console.log('📨 API Response status:', response.status, response.statusText)

      if (!response.ok) {
        const errorData = await response.json()
        console.error('❌ API Error Response:', errorData)
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      console.log('✅ API Success Response:', result)
      return result
    } catch (error) {
      console.error('❌ Error parsing PDF:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        processingTimeMs: 0
      }
    }
  }

  /**
   * Parse multiple PDF application files using AI with owner detection
   */
  async parseMultipleApplications(files: File[]): Promise<AiParsingResult> {
    console.log('🌐 AiPdfParsingService.parseMultipleApplications called with files:', files.map(f => f.name))
    
    try {
      const formData = new FormData()
      files.forEach((file, index) => {
        formData.append('applicationFiles', file)
      })

      console.log('📡 Making API call to:', `${this.baseUrl}/parse-multiple-applications`)
      
      const response = await fetch(`${this.baseUrl}/parse-multiple-applications`, {
        method: 'POST',
        body: formData,
        credentials: 'include' // Include session cookies
      })

      console.log('📨 API Response status:', response.status, response.statusText)

      if (!response.ok) {
        const errorData = await response.json()
        console.error('❌ API Error Response:', errorData)
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      console.log('✅ API Success Response:', result)
      return result
    } catch (error) {
      console.error('❌ Error parsing multiple PDFs:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        processingTimeMs: 0
      }
    }
  }

  /**
   * Simulate progress for UI feedback (since parsing happens on backend)
   */
  createProgressSimulator(
    onProgress: (progress: number, estimatedTimeMs?: number) => void,
    totalDurationMs: number = 15000 // 15 seconds estimated
  ) {
    const interval = 500 // Update every 500ms
    const totalSteps = totalDurationMs / interval
    let currentStep = 0

    const progressInterval = setInterval(() => {
      currentStep++
      
      // Non-linear progress: faster at start, slower near end
      const linearProgress = currentStep / totalSteps
      const easedProgress = Math.min(95, linearProgress * 100 * (2 - linearProgress))
      
      const remainingTime = Math.max(0, totalDurationMs - (currentStep * interval))
      
      onProgress(easedProgress, remainingTime)
      
      if (currentStep >= totalSteps) {
        clearInterval(progressInterval)
      }
    }, interval)

    return () => clearInterval(progressInterval)
  }

  /**
   * Get estimated processing time based on file size
   */
  getEstimatedProcessingTime(fileSizeBytes: number): number {
    // Base time: 5 seconds + additional time based on file size
    const baseDurationMs = 5000
    const additionalDurationMs = Math.min(15000, fileSizeBytes / 1024 / 1024 * 3000) // 3 seconds per MB, max 15 seconds
    
    return baseDurationMs + additionalDurationMs
  }
}

export const aiPdfParsingService = new AiPdfParsingService()
